// LOBBY PLAYGROUND and PODIUM (§6.1, §6.6, §12.5): a printed cardboard toy-box lid on its box,
// floating in a sea of lilac clouds among giant crayons and alphabet blocks. The Lobby adds the
// READY pad (fill ring), three printed vote-card pads, the punching-dummy posts and the toy-red
// vending machine; the Podium adds gold/silver/bronze pedestals with big numerals, a red carpet and
// a floating CHAMPIONS marquee whose bulbs chase on the beat and whose lamps spotlight the winners.
import {
  BoxGeometry, BufferAttribute, Color, ConeGeometry, CylinderGeometry, DoubleSide, Group, Matrix4, Mesh, PlaneGeometry, Quaternion, ShaderMaterial,
  SphereGeometry, TorusGeometry, UniformsLib, UniformsUtils, Vector3, type BufferGeometry, type IUniform, type Texture,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { TUNE } from '@shared/constants';
import { EV } from '@shared/events';
import { ARENAS, MapId, ModeId } from '@shared/ids';
import { LID } from '@shared/maps/lid';
import { TUNING as LOBBY_TUNING } from '@shared/maps/lobby';
import { TUNING as PODIUM_TUNING } from '@shared/maps/podium';
import type { RoundCard } from '@shared/protocol/messages';
import { finishBatch } from '../MapView';
import { Batch, mergeKeep, roundedRectShape, seeded, slab } from '../kit/geometry';
import { surfaceMaterial } from '../kit/materials';
import { canvasTexture, DISPLAY_FONT, liveTexture, roundRect, starPath, stickerText, type Ctx2D } from '../kit/textures';
import { B, type Arena, type ArenaFrame, type ArenaKit, type ArenaLayout } from './types';

const PAD = LOBBY_TUNING.readyPad;
const VOTE = LOBBY_TUNING.votePads;
const VEND = LOBBY_TUNING.vending;
const LID_THICK = 0.4;
const BOX_INSET = 0.35;
const BOX_DEPTH = 3.6;
/** Lid print canvas: px per meter. */
const PRINT_PPM = 64;
const INK = '#2A1A3A';
const STAR = '#FFF1C9';
const TOY_RED = '#FF5A4E';
const CREAM = '#FFF6E9';
const POSITIVE = '#3DDC97';

const MODE_NAMES: Readonly<Record<number, string>> = { [ModeId.ROYALE]: 'BONK ROYALE', [ModeId.HOT_POTATO]: 'HOT POTATO', [ModeId.CROWN_GRAB]: 'CROWN GRAB' };
const MODE_COLORS: Readonly<Record<number, string>> = { [ModeId.ROYALE]: '#FF5A4E', [ModeId.HOT_POTATO]: '#FF9F43', [ModeId.CROWN_GRAB]: '#FFC93C' };
const MAP_NAMES: Readonly<Record<number, string>> = {
  [MapId.TEACUP]: 'TIPSY TEACUP', [MapId.PINBALL]: 'PINBALL PANIC', [MapId.CAKE]: 'CRUMBLE CAKE', [MapId.SPIN]: 'SPIN CYCLE',
  [MapId.LOBBY]: 'PLAYGROUND', [MapId.PODIUM]: 'PODIUM',
};

// ---- Lid print ---------------------------------------------------------------------------------

/** World (sim) → print canvas pixels (the lid occupies the top 768 rows of a 1024² canvas). */
function px(x: number): number {
  return (x + LID.hx) * PRINT_PPM;
}
function py(y: number): number {
  return (y + LID.hy) * PRINT_PPM;
}

function paintLidPrint(g: Ctx2D, seed: number, lobby: boolean): void {
  const rnd = seeded(seed + 101);
  const W = 1024;
  const H = 768;
  g.clearRect(0, 0, W, 1024);
  // Star print, avoiding the pads.
  g.fillStyle = STAR;
  for (let i = 0; i < 170; i++) {
    const x = rnd() * W;
    const y = rnd() * H;
    const r = 6 + rnd() * 14;
    g.globalAlpha = 0.55 + rnd() * 0.35;
    g.save();
    g.translate(x, y);
    g.rotate(rnd() * Math.PI);
    starPath(g, 0, 0, r);
    g.restore();
    g.fill();
  }
  g.globalAlpha = 1;
  // Dashed play-area line inset 0.45 m.
  g.save();
  g.setLineDash([26, 18]);
  g.lineWidth = 7;
  g.strokeStyle = STAR;
  g.globalAlpha = 0.9;
  roundRect(g, 0.45 * PRINT_PPM, 0.45 * PRINT_PPM, W - 0.9 * PRINT_PPM, H - 0.9 * PRINT_PPM, 1.1 * PRINT_PPM);
  g.stroke();
  g.restore();
  // Stamps.
  const stamp = (text: string, x: number, y: number, size: number, rot: number, color: string): void => {
    g.save();
    g.translate(px(x), py(y));
    g.rotate(rot);
    g.globalAlpha = 0.78;
    g.font = `${size}px ${DISPLAY_FONT}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const w = g.measureText(text).width + size * 0.8;
    g.lineWidth = size * 0.12;
    g.strokeStyle = color;
    roundRect(g, -w / 2, -size * 0.75, w, size * 1.5, size * 0.3);
    g.stroke();
    g.fillStyle = color;
    g.fillText(text, 0, size * 0.05);
    g.restore();
  };
  stamp('THIS SIDE UP', -5.6, -4.9, 26, -0.08, TOY_RED);
  // Arrows by the stamp.
  g.save();
  g.fillStyle = TOY_RED;
  g.globalAlpha = 0.78;
  for (const ax of [-7.3, -7.0]) {
    g.beginPath();
    g.moveTo(px(ax), py(-5.4));
    g.lineTo(px(ax) + 10, py(-5.0));
    g.lineTo(px(ax) - 10, py(-5.0));
    g.closePath();
    g.fill();
    g.fillRect(px(ax) - 4, py(-5.0), 8, 18);
  }
  g.restore();
  stamp('FRAGILE: HANDLE WITH GIGGLES', 4.6, 5.25, 18, 0.05, TOY_RED);
  stamp('TOY BOX Nº 8', 6.2, -5.1, 20, 0.1, INK);
  // Faded maker's logo in the middle of the lid (the Podium's carpet runs through there).
  g.save();
  g.globalAlpha = 0.28;
  g.translate(px(lobby ? 0 : -4.6), py(lobby ? 0.9 : 3.6));
  if (!lobby) g.scale(0.55, 0.55);
  g.rotate(-0.04);
  g.font = `${78}px ${DISPLAY_FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = CREAM;
  g.fillText('SHOVE OFF!', 0, 0);
  g.font = `${22}px ${DISPLAY_FONT}`;
  g.fillText('PARTY TOY CO.', 0, 52);
  g.restore();
  if (lobby) {
    // Dummy posts: target rings. Vending trigger: a dashed coin circle.
    for (const p of LOBBY_TUNING.dummyPosts) {
      for (let k = 3; k >= 1; k--) {
        g.beginPath();
        g.arc(px(p.x), py(p.y), k * 0.24 * PRINT_PPM, 0, Math.PI * 2);
        g.fillStyle = k % 2 === 1 ? TOY_RED : CREAM;
        g.globalAlpha = 0.85;
        g.fill();
      }
    }
    g.globalAlpha = 1;
    g.save();
    g.setLineDash([14, 10]);
    g.lineWidth = 6;
    g.strokeStyle = '#FFC93C';
    g.beginPath();
    g.arc(px(VEND.triggerX), py(VEND.triggerY), VEND.triggerRadius * PRINT_PPM, 0, Math.PI * 2);
    g.stroke();
    g.restore();
    g.font = `22px ${DISPLAY_FONT}`;
    g.textAlign = 'center';
    g.fillStyle = '#FFC93C';
    g.fillText('GRAB A GOODIE', px(VEND.triggerX), py(VEND.triggerY + 0.35));
    // READY label under the pad (the pad itself is a translucent ring decal).
    stickerText(g, 'READY?', px(PAD.x), py(PAD.y), 44, POSITIVE);
  }
  // Box front label strip (rows 800–1024): "TOYS!" lettering and tape.
  g.fillStyle = '#C89058';
  g.fillRect(0, 784, W, 240);
  g.fillStyle = 'rgba(255, 246, 233, 0.35)';
  g.fillRect(0, 784, W, 10);
  g.font = `150px ${DISPLAY_FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineWidth = 16;
  g.strokeStyle = INK;
  g.strokeText('TOYS!', W / 2, 905);
  g.fillStyle = TOY_RED;
  g.fillText('TOYS!', W / 2, 900);
  g.font = `34px ${DISPLAY_FONT}`;
  g.fillStyle = STAR;
  g.fillText('★ AGES 3 TO 103 ★', W / 2 - 300, 980);
  g.fillText('★ 8 BEANS INCLUDED ★', W / 2 + 290, 980);
}

const LID_ALBEDO = /* glsl */ `
  float fib = envNoise(vec2(envObj.x * 1.2, envObj.z * 38.0)) * 0.55 + envNoise(envObj.xz * 8.0) * 0.45;
  vec3 board = albedo * (0.9 + 0.15 * fib);
  board *= 0.93 + 0.1 * envFbm(envObj.xz * 0.3 + 3.0, 3);
  if (envNw.y > 0.6 && envObj.y > -0.05) {
    vec2 uv = vec2((envObj.x + ${LID.hx.toFixed(1)}) / ${(LID.hx * 2).toFixed(1)}, 1.0 - 0.75 * (envObj.z + ${LID.hy.toFixed(1)}) / ${(LID.hy * 2).toFixed(1)});
    vec4 print = texture2D(uPrint, uv);
    float wear = smoothstep(0.2, 0.65, envNoise(envObj.xz * 2.6 + 4.0)) * 0.25;
    board = mix(board, print.rgb, print.a * (1.0 - wear));
    // Depth: the board darkens toward the lid's edge and catches the light on its bevel.
    vec2 lq = abs(envObj.xz) - vec2(${(LID.hx - LID.r).toFixed(2)}, ${(LID.hy - LID.r).toFixed(2)});
    float ld = length(max(lq, 0.0)) + min(max(lq.x, lq.y), 0.0) - ${LID.r.toFixed(2)};
    board *= 0.9 + 0.1 * smoothstep(0.0, -1.6, ld);
    board *= 1.0 + 0.1 * smoothstep(-0.14, -0.04, ld);
  } else if (envObj.y > -${LID_THICK.toFixed(2)} - 0.01) {
    // The lid's edge: corrugated flutes in the middle of the board.
    float flute = sin((envObj.x + envObj.z) * 55.0);
    float band = smoothstep(-0.33, -0.3, envObj.y) * smoothstep(-0.06, -0.09, envObj.y);
    board = mix(board * 0.92, board * (0.8 + 0.14 * flute), band);
  } else if (envNw.z > 0.6) {
    // Box front: the printed label strip and packing tape.
    float h = clamp((envObj.y + ${LID_THICK.toFixed(2)} + 0.25) / -1.9, 0.0, 1.0);
    vec2 uv = vec2((envObj.x + ${(LID.hx - BOX_INSET).toFixed(2)}) / ${((LID.hx - BOX_INSET) * 2).toFixed(2)}, 0.235 - h * 0.22);
    vec4 label = texture2D(uPrint, uv);
    float inLabel = step(0.0, uv.y) * step(h, 0.999);
    board = mix(board, label.rgb, label.a * inLabel * 0.95);
  }
  float tape = smoothstep(0.62, 0.6, abs(envObj.x)) * step(envObj.y, -${(LID_THICK + 0.01).toFixed(2)});
  board = mix(board, vec3(0.93, 0.85, 0.66), tape * 0.55);
  albedo = board;
`;

// ---- READY pad ---------------------------------------------------------------------------------

const PAD_VERTEX = /* glsl */ `
varying vec2 vP;
#include <common>
#include <fog_pars_vertex>
void main() {
  vP = position.xz;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;
const PAD_FRAGMENT = /* glsl */ `
uniform float uProgress;
uniform float uBeat;
uniform float uFlash;
uniform float uRadius;
uniform vec3 uColor;
varying vec2 vP;
#include <common>
#include <fog_pars_fragment>
void main() {
  float r = length(vP) / uRadius;
  if (r > 1.0) discard;
  float a = atan(vP.x, -vP.y) / 6.2831853 + 0.5;
  float ring = smoothstep(0.75, 0.78, r) * smoothstep(1.0, 0.96, r);
  float fill = ring * step(a, uProgress);
  float track = ring * (1.0 - step(a, uProgress));
  float pulse = 0.5 + 0.5 * cos(6.2831853 * uBeat);
  float disc = 1.0 - smoothstep(0.7, 0.75, r);
  float halo = smoothstep(0.45, 0.74, r) * disc;
  float tick = smoothstep(0.72, 0.735, r) * smoothstep(0.75, 0.735, r);
  vec3 col = uColor * (track * 0.55 + fill * 1.9 + disc * 0.16 + halo * (0.25 + 0.2 * pulse) + tick * 0.8) + vec3(1.0) * (fill * 0.45 + uFlash * (ring + disc * 0.5));
  float alpha = clamp(track * 0.85 + fill + disc * 0.2 + halo * (0.3 + 0.2 * pulse) + tick * 0.6 + uFlash * ring, 0.0, 1.0);
  gl_FragColor = vec4(col, alpha);
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

// ---- Vote cards ----------------------------------------------------------------------------------

const CARD_W = 512;
const CARD_H = 362;

function paintMapGlyph(g: Ctx2D, mapId: number, x: number, y: number, s: number): void {
  g.save();
  g.translate(x, y);
  g.fillStyle = CREAM;
  g.strokeStyle = INK;
  g.lineWidth = s * 0.08;
  g.beginPath();
  switch (mapId) {
    case MapId.TEACUP:
      g.ellipse(0, s * 0.15, s * 0.75, s * 0.32, 0, 0, Math.PI * 2);
      g.fill(); g.stroke();
      g.beginPath();
      g.moveTo(-s * 0.35, -s * 0.35); g.lineTo(s * 0.35, -s * 0.35); g.lineTo(s * 0.25, s * 0.1); g.lineTo(-s * 0.25, s * 0.1); g.closePath();
      break;
    case MapId.PINBALL:
      g.arc(0, 0, s * 0.42, 0, Math.PI * 2);
      g.fillStyle = '#CFD8E3';
      break;
    case MapId.CAKE:
      for (let k = 0; k < 6; k++) {
        const a = (k * Math.PI) / 3;
        if (k === 0) g.moveTo(Math.cos(a) * s * 0.5, Math.sin(a) * s * 0.5);
        else g.lineTo(Math.cos(a) * s * 0.5, Math.sin(a) * s * 0.5);
      }
      g.closePath();
      g.fillStyle = '#FFB3C7';
      break;
    case MapId.SPIN:
      g.arc(0, 0, s * 0.5, 0, Math.PI * 2);
      g.fillStyle = '#4FD1C5';
      g.fill(); g.stroke();
      g.beginPath();
      g.rect(-s * 0.5, -s * 0.07, s, s * 0.14);
      g.fillStyle = CREAM;
      break;
    default:
      g.rect(-s * 0.5, -s * 0.35, s, s * 0.7);
  }
  g.fill();
  g.stroke();
  g.restore();
}

function paintModeGlyph(g: Ctx2D, modeId: number, x: number, y: number, s: number): void {
  g.save();
  g.translate(x, y);
  g.lineWidth = s * 0.09;
  g.strokeStyle = INK;
  g.lineJoin = 'round';
  if (modeId === ModeId.CROWN_GRAB) {
    g.beginPath();
    g.moveTo(-s * 0.5, s * 0.3); g.lineTo(-s * 0.5, -s * 0.25); g.lineTo(-s * 0.22, s * 0.02); g.lineTo(0, -s * 0.4);
    g.lineTo(s * 0.22, s * 0.02); g.lineTo(s * 0.5, -s * 0.25); g.lineTo(s * 0.5, s * 0.3); g.closePath();
    g.fillStyle = '#FFD24A';
  } else if (modeId === ModeId.HOT_POTATO) {
    g.beginPath();
    g.ellipse(0, s * 0.05, s * 0.48, s * 0.36, -0.3, 0, Math.PI * 2);
    g.fillStyle = '#E0A35A';
    g.fill(); g.stroke();
    g.beginPath();
    g.moveTo(s * 0.25, -s * 0.25); g.quadraticCurveTo(s * 0.4, -s * 0.6, s * 0.55, -s * 0.45);
    g.strokeStyle = INK;
    g.stroke();
    g.beginPath();
    starPath(g, s * 0.58, -s * 0.5, s * 0.16);
    g.fillStyle = '#FFC93C';
  } else {
    // Bonk burst.
    starPath(g, 0, 0, s * 0.55, 0.55);
    g.fillStyle = '#FFF1C9';
  }
  g.fill();
  g.stroke();
  g.restore();
}

function paintCard(g: Ctx2D, i: number, card: RoundCard, selected: boolean): void {
  const x0 = i * CARD_W;
  g.clearRect(x0, 0, CARD_W, CARD_H);
  g.fillStyle = MODE_COLORS[card.mode] ?? TOY_RED;
  roundRect(g, x0 + 6, 6, CARD_W - 12, CARD_H - 12, 34);
  g.fill();
  g.lineWidth = 10;
  g.strokeStyle = selected ? '#FFFFFF' : CREAM;
  g.stroke();
  // Diagonal sheen stripes.
  g.save();
  g.clip();
  g.globalAlpha = 0.12;
  g.fillStyle = '#FFFFFF';
  for (let k = -2; k < 6; k++) {
    g.beginPath();
    g.moveTo(x0 + k * 110, 0); g.lineTo(x0 + k * 110 + 50, 0); g.lineTo(x0 + k * 110 - 80, CARD_H); g.lineTo(x0 + k * 110 - 130, CARD_H);
    g.fill();
  }
  g.restore();
  paintModeGlyph(g, card.mode, x0 + 130, 100, 104);
  paintMapGlyph(g, card.map, x0 + 382, 100, 104);
  stickerText(g, MODE_NAMES[card.mode] ?? 'ROYALE', x0 + CARD_W / 2, 222, 66, '#FFFFFF');
  g.font = `44px ${DISPLAY_FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = INK;
  g.fillText(MAP_NAMES[card.map] ?? '', x0 + CARD_W / 2, 302, CARD_W - 60);
}

function defaultCards(seed: number): RoundCard[] {
  const modes = [ModeId.ROYALE, ModeId.HOT_POTATO, ModeId.CROWN_GRAB];
  return modes.map((mode, i) => ({ mode, map: ARENAS[(seed + i) % ARENAS.length] }));
}

// ---- Shared lid + box ------------------------------------------------------------------------

function lidAndBox(kit: ArenaKit, root: Group, print: Texture): void {
  const env = kit.env;
  const lidMat = surfaceMaterial(env, kit.ramp, { key: 'lobby-lid', color: kit.palette.base, rim: 0.25, gloss: 0.06, albedo: LID_ALBEDO, uniforms: { uPrint: { value: print } } });
  const lidGeo = slab(roundedRectShape(LID.hx, LID.hy, LID.r), 0, LID_THICK, 0.05, kit.seg(20, 8), 2);
  const lid = new Mesh(lidGeo, lidMat);
  lid.name = 'lid';
  lid.receiveShadow = true;
  lid.castShadow = true;
  lid.userData.wp6Owned = true;
  root.add(lid);
  kit.batch.add(B.hullBold, lidGeo.clone(), null);
  // The box under the lid (darker kraft), dipping into the clouds.
  const boxGeo = slab(roundedRectShape(LID.hx - BOX_INSET, LID.hy - BOX_INSET, LID.r - 0.2), -LID_THICK, BOX_DEPTH, 0, kit.seg(16, 6), 1);
  const boxMat = surfaceMaterial(env, kit.ramp, {
    key: 'lobby-box', color: new Color(kit.palette.base).multiplyScalar(0.8), rim: 0.3, albedo: LID_ALBEDO, uniforms: { uPrint: { value: print } },
  });
  const box = new Mesh(boxGeo, boxMat);
  box.name = 'toybox';
  box.receiveShadow = true;
  box.userData.wp6Owned = true;
  root.add(box);
  kit.batch.add(B.hull, boxGeo.clone(), null);
  (root.userData.mats ??= []).push(lidMat, boxMat);
}

/** Giant crayons, alphabet blocks and a bouncy ball, half-sunk in the cloud sea. */
function toyBackground(kit: ArenaKit, podium: boolean): void {
  const rnd = seeded(kit.seed * 3 + (podium ? 11 : 5));
  const crayonColors = ['#FF5A4E', '#4CB8FF', '#3DDC97', '#FFC93C', '#9B6BFF', '#FF8FB1'];
  const crayons: [number, number, number, number, number][] = [
    // x, z(sim y), y, yaw, tilt
    [-21, -16, -1.5, 0.6, 0.55], [19, -19, -0.5, -0.9, -0.45], [-27, 4, -2.0, 1.4, 0.3], [26, 6, -1.0, 2.6, -0.6], [3, -31, 0.5, 0.2, 0.25],
  ];
  crayons.forEach(([x, z, y, yaw, tilt], i) => {
    const color = crayonColors[i % crayonColors.length];
    const len = 9 + rnd() * 4;
    const r = 0.75;
    const m = new Matrix4().makeRotationY(yaw).multiply(new Matrix4().makeRotationZ(Math.PI / 2 + tilt)).setPosition(x, y, z);
    const body = new CylinderGeometry(r, r, len, kit.seg(14, 8));
    kit.batch.add(B.bg, body, m, { color });
    const wrap = new CylinderGeometry(r * 1.02, r * 1.02, len * 0.6, kit.seg(14, 8));
    kit.batch.add(B.bg, wrap, m, { color: new Color(color).lerp(new Color(CREAM), 0.55) });
    for (const off of [-0.27, 0.27]) {
      const band = new CylinderGeometry(r * 1.04, r * 1.04, 0.22, kit.seg(14, 8));
      kit.batch.add(B.bg, band, m.clone().multiply(new Matrix4().makeTranslation(0, off * len, 0)), { color: INK });
    }
    const tip = new ConeGeometry(r, 1.6, kit.seg(14, 8));
    kit.batch.add(B.bg, tip, m.clone().multiply(new Matrix4().makeTranslation(0, len / 2 + 0.8, 0)), { color });
    const shave = new CylinderGeometry(r * 0.98, r, 0.5, kit.seg(14, 8));
    kit.batch.add(B.bg, shave, m.clone().multiply(new Matrix4().makeTranslation(0, len / 2 - 0.05, 0)), { color: '#F2D7B0' });
    if (kit.rich) kit.batch.add(B.bgHull, new CylinderGeometry(r, r, len, 10), m);
  });
  const blockColors = ['#FF5A4E', '#4CB8FF', '#FFC93C', '#3DDC97', '#9B6BFF'];
  const blocks: [number, number, number, number][] = [[-16, -22, -2.2, 2.6], [-12.5, -24, -2.6, 2.2], [15, -25, -2.0, 3.0], [24, -10, -2.4, 2.4], [-24, -8, -1.4, 2.8], [10, -34, -1.8, 2.6]];
  blocks.forEach(([x, z, y, s], i) => {
    const m = new Matrix4().makeRotationY(rnd() * 1.4).multiply(new Matrix4().makeRotationX((rnd() - 0.5) * 0.4)).setPosition(x, y, z);
    const cube = new RoundedBoxGeometry(s, s, s, 2, s * 0.08);
    kit.batch.add(B.bg, cube, m, { color: CREAM });
    // Colored inset panels on each face (a raised plate per face).
    const c = blockColors[i % blockColors.length];
    for (const [ax, ay, az] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
      const plate = new BoxGeometry(ax !== 0 ? 0.06 : s * 0.74, ay !== 0 ? 0.06 : s * 0.74, az !== 0 ? 0.06 : s * 0.74);
      kit.batch.add(B.bg, plate, m.clone().multiply(new Matrix4().makeTranslation(ax * s * 0.5, ay * s * 0.5, az * s * 0.5)), { color: c });
    }
    if (kit.rich) kit.batch.add(B.bgHull, new RoundedBoxGeometry(s, s, s, 1, s * 0.08), m);
  });
  // A striped bouncy ball.
  const ball = new SphereGeometry(2.4, kit.seg(24, 10), kit.seg(16, 8));
  kit.batch.add(B.bg, ball, new Matrix4().makeTranslation(21, -1.2, -20), { color: '#FF8FB1' });
  const stripe = new TorusGeometry(2.42, 0.32, 6, kit.seg(28, 12));
  kit.batch.add(B.bg, stripe, new Matrix4().makeRotationZ(0.5).setPosition(21, -1.2, -20), { color: '#FFF1C9' });
}

function lobbyLayout(kit: ArenaKit, podium: boolean): ArenaLayout {
  const p = kit.palette;
  return {
    sky: { top: p.skyTop, bottom: p.skyBottom, cloud: '#FFF4EC', clouds: 0.75, stars: 0, sunGlow: 0.9 },
    voidColors: { a: p.void, b: '#F6F2FF', c: p.skyTop },
    voidEdge: 10,
    splash: { ring: '#FFFFFF', edge: '#E4DDFF' },
    audience: {
      cx: 0, cy: 0, rx: LID.hx + 5.5, ry: LID.hy + 5.5, y: -1.1, plank: '#C98F55', trim: TOY_RED,
      fur: ['#FF8FB1', '#9DE0C8', '#FFC93C', '#B9A4FF', '#7FD0FF', '#FFB38A', '#FFFFFF'],
    },
    // The Podium celebrates: confetti rains over the pedestals; the Lobby's drifts in the fog band.
    confetti: podium
      ? { colors: ['#FFD24A', '#FF4F9A', '#4CB8FF', '#3DDC97', '#CFD8E3'], r0: 1.5, r1: 10, y0: 0.1, y1: 6.5 }
      : { colors: ['#FF5A4E', '#FFC93C', '#4CB8FF', '#3DDC97', '#FF8FB1'], r0: 13, r1: 34, y0: -3, y1: 16 },
    shadowY: [-0.5, 3.0],
  };
}

// ---- Lobby -------------------------------------------------------------------------------------

export function buildLobby(kit: ArenaKit): Arena {
  const root = new Group();
  root.name = 'arena:lobby';
  const owned: { dispose(): void }[] = [];
  const print = canvasTexture(1024, 1024, (g) => paintLidPrint(g, kit.seed, true));
  owned.push(print);
  lidAndBox(kit, root, print);
  toyBackground(kit, false);

  // READY pad decal.
  const R = PAD.radius;
  const padMat = new ShaderMaterial({
    uniforms: {
      ...UniformsUtils.clone(UniformsLib.fog), uProgress: { value: 0 }, uBeat: kit.env.uBeat, uFlash: { value: 0 }, uRadius: { value: R },
      uColor: { value: new Color(POSITIVE) },
    },
    vertexShader: PAD_VERTEX, fragmentShader: PAD_FRAGMENT, transparent: true, depthWrite: false, fog: true,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  const padGeo = new PlaneGeometry(R * 2, R * 2);
  padGeo.rotateX(-Math.PI / 2);
  const pad = new Mesh(padGeo, padMat);
  pad.position.set(PAD.x, 0.012, PAD.y);
  pad.renderOrder = 3;
  pad.name = 'ready-pad';
  root.add(pad);
  owned.push(padGeo, padMat);

  // Vote-card pads: one atlas texture, one mesh.
  const cardTex = liveTexture(CARD_W * 3, CARD_H);
  owned.push(cardTex);
  let cards = defaultCards(kit.seed);
  let selected = -1;
  const repaintCards = (): void => cardTex.paint((g) => cards.forEach((c, i) => paintCard(g, i, c, i === selected)));
  repaintCards();
  const cardGeos: BufferGeometry[] = [];
  const CW = VOTE.width;
  const CH = VOTE.depth;
  LOBBY_CARD_X.forEach((cx, i) => {
    const g = slab(roundedRectShape(CW / 2, CH / 2, 0.28), 0.07, 0.1, 0.03, 8, 2);
    kit.batch.add(B.hull, g.clone().translate(cx, 0, LOBBY_CARD_Y), null);
    const pos = g.getAttribute('position');
    const uv = g.getAttribute('uv');
    for (let k = 0; k < pos.count; k++) {
      const u = Math.min(1, Math.max(0, (pos.getX(k) + CW / 2) / CW));
      const v = 1 - Math.min(1, Math.max(0, (pos.getZ(k) + CH / 2) / CH));
      uv.setXY(k, (i + u) / 3, v);
    }
    g.translate(cx, 0, LOBBY_CARD_Y);
    cardGeos.push(g);
  });
  const cardGeo = mergeKeep(cardGeos, ['position', 'normal', 'uv']);
  const cardGlow = { value: [0, 0, 0] };
  const cardMat = surfaceMaterial(kit.env, kit.ramp, {
    key: 'lobby-cards', map: cardTex.texture, rim: 0.2, gloss: 0.3,
    uniforms: { uCardGlow: cardGlow },
    pars: 'uniform float uCardGlow[3];',
    emissive: `
      int idx = int(clamp(floor(envUv.x * 3.0), 0.0, 2.0));
      float sel = uCardGlow[idx];
      vec2 cuv = vec2(fract(envUv.x * 3.0), envUv.y);
      float edge = 1.0 - smoothstep(0.0, 0.06, min(min(cuv.x, 1.0 - cuv.x), min(cuv.y, 1.0 - cuv.y) * 1.4));
      glow += vec3(1.0, 0.95, 0.8) * sel * (0.18 + edge * 0.9);`,
  });
  const cardMesh = new Mesh(cardGeo, cardMat);
  cardMesh.receiveShadow = true;
  cardMesh.name = 'vote-cards';
  root.add(cardMesh);
  owned.push(cardGeo, cardMat);

  // Vending machine (its own group so it can shake on VEND).
  const vend = new Group();
  vend.name = 'vending';
  const V = LOBBY_TUNING.vending;
  vend.position.set(V.x, 0, V.y);
  const vb = new Batch();
  const body = new RoundedBoxGeometry(V.width, V.top, V.depth, 3, 0.14);
  vb.add(B.gloss, body, new Matrix4().makeTranslation(0, V.top / 2, 0), { color: TOY_RED, ao: [0, 0.6, 0.7] });
  vb.add(B.hull, new RoundedBoxGeometry(V.width, V.top, V.depth, 1, 0.14), new Matrix4().makeTranslation(0, V.top / 2, 0));
  // Window frame + backlit window.
  const frame = new RoundedBoxGeometry(1.36, 1.16, 0.08, 2, 0.05);
  vb.add(B.toon, frame, new Matrix4().makeTranslation(-0.2, 1.2, V.depth / 2), { color: CREAM });
  const win = new PlaneGeometry(1.2, 1.0);
  vb.add(B.glow, win, new Matrix4().makeTranslation(-0.2, 1.2, V.depth / 2 + 0.045), { color: '#BFF3FF' });
  // Shelf rows with a mini item display.
  const itemColors = ['#FFE14D', '#2A1A3A', '#FF5A4E', '#FF8FB1', '#9B6BFF', '#3DDC97'];
  for (let row = 0; row < 2; row++) {
    const sy = 0.9 + row * 0.45;
    vb.add(B.toon, new BoxGeometry(1.2, 0.04, 0.12), new Matrix4().makeTranslation(-0.2, sy - 0.14, V.depth / 2 + 0.05), { color: '#9AA9B8' });
    for (let k = 0; k < 3; k++) {
      const c = itemColors[row * 3 + k];
      const item = k === 1 ? new SphereGeometry(0.12, 10, 8) : new RoundedBoxGeometry(0.2, 0.2, 0.12, 1, 0.05);
      vb.add(B.toon, item, new Matrix4().makeTranslation(-0.6 + k * 0.4, sy, V.depth / 2 + 0.07), { color: c });
    }
  }
  // Button panel, coin slot, dispenser tray.
  vb.add(B.toon, new RoundedBoxGeometry(0.38, 1.0, 0.1, 2, 0.04), new Matrix4().makeTranslation(0.72, 1.2, V.depth / 2), { color: CREAM });
  for (let k = 0; k < 3; k++) {
    vb.add(B.glow, new CylinderGeometry(0.07, 0.07, 0.05, 12).rotateX(Math.PI / 2), new Matrix4().makeTranslation(0.72, 1.48 - k * 0.2, V.depth / 2 + 0.06), { color: ['#3DDC97', '#FFC93C', '#4CB8FF'][k] });
  }
  vb.add(B.toon, new BoxGeometry(0.12, 0.03, 0.04), new Matrix4().makeTranslation(0.72, 0.85, V.depth / 2 + 0.06), { color: INK });
  vb.add(B.toon, new RoundedBoxGeometry(1.3, 0.34, 0.14, 2, 0.06), new Matrix4().makeTranslation(-0.2, 0.32, V.depth / 2 + 0.02), { color: INK });
  // Marquee sign.
  const marquee = new RoundedBoxGeometry(1.9, 0.42, 0.2, 2, 0.1);
  vb.add(B.toon, marquee, new Matrix4().makeTranslation(0, V.top + 0.24, 0.05), { color: '#FFC93C' });
  vb.add(B.hull, new RoundedBoxGeometry(1.9, 0.42, 0.2, 1, 0.1), new Matrix4().makeTranslation(0, V.top + 0.24, 0.05));
  for (let k = 0; k < 9; k++) {
    vb.add(B.glow, new SphereGeometry(0.045, 6, 4), new Matrix4().makeTranslation(-0.84 + k * 0.21, V.top + 0.24, 0.16), { color: '#FFFFFF' });
  }
  // Little feet.
  for (const fx of [-0.8, 0.8]) vb.add(B.toon, new CylinderGeometry(0.12, 0.14, 0.12, 10), new Matrix4().makeTranslation(fx, 0.02, 0), { color: INK });
  finishBatch(kit, vb, vend);
  const marqueeLabel = canvasTexture(512, 112, (g, w, h) => stickerText(g, 'GOODIES!', w / 2, h / 2, 64, TOY_RED));
  owned.push(marqueeLabel);
  const labelMat = surfaceMaterial(kit.env, kit.ramp, { key: 'lobby-marquee', map: marqueeLabel, transparent: true, rim: 0, side: DoubleSide });
  labelMat.alphaTest = 0.4;
  const label = new Mesh(new PlaneGeometry(1.7, 0.37), labelMat);
  label.position.set(0, V.top + 0.24, 0.155);
  vend.add(label);
  owned.push(label.geometry, labelMat);
  root.add(vend);
  let vendShake = 0;

  const layout = lobbyLayout(kit, false);
  return {
    root, layout,
    update(f: ArenaFrame) {
      const v = f.view;
      // READY pad progress: the local bean while it stands on the pad, else the fullest bean on it.
      let progress = 0;
      let localOn = false;
      for (const b of v.beans) {
        if (!b.active || b.out) continue;
        const on = Math.hypot(b.x - PAD.x, b.y - PAD.y) <= R;
        if (!on) continue;
        const p = Math.min(1, b.readyTicks / TUNE.lobby.readyTicks);
        if (b.isLocal) {
          progress = p;
          localOn = true;
        } else if (!localOn) progress = Math.max(progress, p);
      }
      const u = padMat.uniforms;
      u.uProgress.value += (progress - u.uProgress.value) * Math.min(1, f.dt * 14);
      u.uFlash.value = Math.max(0, u.uFlash.value - f.dt * 2.5);
      // Vote card under the local bean glows.
      let sel = -1;
      const me = v.localSlot < v.beans.length ? v.beans[v.localSlot] : null;
      if (me && me.active && !me.out) {
        LOBBY_CARD_X.forEach((cx, i) => {
          if (Math.abs(me.x - cx) <= CW / 2 && Math.abs(me.y - LOBBY_CARD_Y) <= CH / 2) sel = i;
        });
      }
      for (let i = 0; i < 3; i++) {
        const target = i === sel ? 0.6 + 0.4 * Math.cos(f.beat * Math.PI * 2) * 0.5 : 0;
        cardGlow.value[i] += (target - cardGlow.value[i]) * Math.min(1, f.dt * 10);
      }
      if (sel !== selected) {
        selected = sel;
        repaintCards();
      }
      // Vending shake.
      vendShake = Math.max(0, vendShake - f.dt * 3);
      const s = vendShake * vendShake;
      vend.rotation.z = Math.sin(f.time * 47) * 0.05 * s;
      vend.position.x = V.x + Math.sin(f.time * 31) * 0.05 * s;
      vend.scale.y = 1 + 0.06 * s * Math.sin(f.time * 23);
    },
    onEvent(ev) {
      if (ev.type === EV.READY_PAD && ev.b === 1) padMat.uniforms.uFlash.value = 1;
      else if (ev.type === EV.VEND) vendShake = 1;
    },
    setVoteCards(next: readonly RoundCard[]) {
      cards = next.slice(0, 3);
      while (cards.length < 3) cards.push(defaultCards(kit.seed)[cards.length]);
      repaintCards();
    },
    dispose() {
      for (const o of owned) o.dispose();
      for (const m of (root.userData.mats ?? []) as { dispose(): void }[]) m.dispose();
    },
  } as Arena & { setVoteCards(c: readonly RoundCard[]): void };
}

const LOBBY_CARD_X = VOTE.centers.map((c) => c.x);
const LOBBY_CARD_Y = VOTE.centers[0].y;


// ---- Podium ------------------------------------------------------------------------------------

const MEDALS = ['#FFD24A', '#CFD8E3', '#E0915A'] as const;
const PANEL_W = 1024;
const PANEL_H = 256;

/** The unwrapped side label of pedestal `i` (u = around, the front at u = 0.5). */
function paintPedestalPanel(g: Ctx2D, i: number, circumference: number, height: number): void {
  const y0 = i * PANEL_H;
  const medal = MEDALS[i];
  g.fillStyle = CREAM;
  g.fillRect(0, y0, PANEL_W, PANEL_H);
  g.fillStyle = medal;
  g.fillRect(0, y0, PANEL_W, PANEL_H * 0.14);
  g.fillRect(0, y0 + PANEL_H * 0.9, PANEL_W, PANEL_H * 0.1);
  // Stars around the drum, compressed to the panel's aspect.
  const pxPerM = PANEL_W / circumference;
  const pyPerM = PANEL_H / height;
  const squash = pxPerM / pyPerM;
  g.fillStyle = medal;
  for (let k = 0; k < 10; k++) {
    if (k === 5) continue;
    g.save();
    g.translate((k + 0.5) * (PANEL_W / 10), y0 + PANEL_H * 0.52);
    g.scale(squash, 1);
    starPath(g, 0, 0, PANEL_H * 0.14);
    g.restore();
    g.fill();
  }
  g.save();
  g.translate(PANEL_W / 2, y0 + PANEL_H * 0.54);
  g.scale(squash * 1.6, 1);
  stickerText(g, String(i + 1), 0, 0, PANEL_H * 0.84, medal);
  g.restore();
}

export function buildPodium(kit: ArenaKit): Arena {
  const root = new Group();
  root.name = 'arena:podium';
  const owned: { dispose(): void }[] = [];
  const print = canvasTexture(1024, 1024, (g) => {
    paintLidPrint(g, kit.seed, false);
    stickerText(g, 'CHAMPIONS', px(0), py(-4.6), 54, '#FFD24A');
  });
  owned.push(print);
  lidAndBox(kit, root, print);
  toyBackground(kit, true);
  // A velvet carpet runner from the front edge to the winners, with gold edging.
  const carpet = new RoundedBoxGeometry(2.0, 0.04, 5.2, 2, 0.015);
  kit.batch.add(B.toon, carpet, new Matrix4().makeTranslation(0, 0.02, 2.95), { color: '#D93445' });
  for (const s of [-1, 1]) {
    kit.batch.add(B.gloss, new RoundedBoxGeometry(0.1, 0.05, 5.2, 1, 0.02), new Matrix4().makeTranslation(s * 1.0, 0.025, 2.95), { color: '#FFD24A' });
  }
  kit.batch.add(B.hull, new RoundedBoxGeometry(2.1, 0.05, 5.2, 1, 0.02), new Matrix4().makeTranslation(0, 0.025, 2.95));

  const peds = PODIUM_TUNING.pedestals;
  const sideTex = canvasTexture(PANEL_W, PANEL_H * 3, (g) => peds.forEach((p, i) => paintPedestalPanel(g, i, Math.PI * 2 * p.r, p.height)));
  owned.push(sideTex);
  const sides: BufferGeometry[] = [];
  const pb = new Batch();
  peds.forEach((p, i) => {
    const h = p.height - 0.08;
    const side = new CylinderGeometry(p.r * 0.97, p.r, h, kit.seg(40, 16), 1, true);
    side.rotateY(Math.PI);
    const uv = side.getAttribute('uv');
    for (let k = 0; k < uv.count; k++) uv.setY(k, (2 - i + uv.getY(k)) / 3);
    side.translate(p.x, h / 2, p.y);
    sides.push(side);
    // Medal cap with a lip, and a base ring.
    const cap = new CylinderGeometry(p.r + 0.06, p.r + 0.06, 0.12, kit.seg(40, 16));
    pb.add(B.gloss, cap, new Matrix4().makeTranslation(p.x, p.height - 0.06, p.y), { color: MEDALS[i] });
    const base = new CylinderGeometry(p.r + 0.1, p.r + 0.14, 0.08, kit.seg(40, 16));
    pb.add(B.toon, base, new Matrix4().makeTranslation(p.x, 0.04, p.y), { color: MEDALS[i] });
    pb.add(B.hullBold, new CylinderGeometry(p.r + 0.06, p.r, p.height, kit.seg(32, 12)), new Matrix4().makeTranslation(p.x, p.height / 2, p.y));
  });
  const sideGeo = mergeKeep(sides, ['position', 'normal', 'uv']);
  const sideMat = surfaceMaterial(kit.env, kit.ramp, { key: 'podium-sides', map: sideTex, rim: 0.35, gloss: 0.25 });
  const sideMesh = new Mesh(sideGeo, sideMat);
  sideMesh.castShadow = true;
  sideMesh.receiveShadow = true;
  root.add(sideMesh);
  owned.push(sideGeo, sideMat);
  finishBatch(kit, pb, root);

  // The ceremony backdrop: a cardboard CHAMPIONS marquee floating beyond the lid's far edge on two
  // balloons (clear of play), its border of bulbs chasing on the beat, and three lamps on its top
  // edge throwing spotlight beams onto the pedestals. DETHRONE turns bulbs and beams red.
  const red = { value: 0 };
  const marquee = buildMarquee(kit, root, owned, red);
  const beamMat = new ShaderMaterial({
    uniforms: { ...UniformsUtils.clone(UniformsLib.fog), uBeat: kit.env.uBeat, uRed: red, uColor: { value: new Color('#FFF1C9') }, uHot: { value: new Color('#FF4D4D') } },
    vertexShader: /* glsl */ `
      varying float vH;
      varying vec3 vN;
      varying vec3 vV;
      #include <common>
      #include <fog_pars_vertex>
      void main() {
        vH = uv.y;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vN = normalize(normalMatrix * normal);
        vV = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      uniform float uBeat;
      uniform float uRed;
      uniform vec3 uColor;
      uniform vec3 uHot;
      varying float vH;
      varying vec3 vN;
      varying vec3 vV;
      #include <common>
      #include <fog_pars_fragment>
      void main() {
        // Soft-edged volume: brightest through the middle of the beam, near the lamp, pulsing on the beat.
        float edge = pow(abs(dot(normalize(vN), normalize(vV))), 2.0);
        float a = edge * (0.34 + 0.3 * vH) * (0.88 + 0.12 * cos(6.2831853 * uBeat));
        vec3 c = mix(uColor, uHot, uRed);
        gl_FragColor = vec4(c * a, a);
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
    transparent: true, depthWrite: false, blending: 2, side: DoubleSide, fog: true,
  });
  const UP3 = new Vector3(0, 1, 0);
  const beams: BufferGeometry[] = peds.map((p, i) => {
    const lamp = marquee.lamps[i];
    const target = new Vector3(p.x, p.height + 0.05, p.y);
    const dir = lamp.clone().sub(target);
    const len = dir.length();
    const c = new CylinderGeometry(0.16, p.r * 1.12, len, kit.seg(24, 12), 1, true);
    c.applyQuaternion(new Quaternion().setFromUnitVectors(UP3, dir.normalize()));
    c.translate((lamp.x + target.x) / 2, (lamp.y + target.y) / 2, (lamp.z + target.z) / 2);
    return c;
  });
  const beamGeo = mergeKeep(beams, ['position', 'normal', 'uv']);
  const beamMesh = new Mesh(beamGeo, beamMat);
  beamMesh.renderOrder = 4;
  beamMesh.name = 'spotlights';
  root.add(beamMesh);
  owned.push(beamGeo, beamMat);

  let dethrone = 0;
  const layout = lobbyLayout(kit, true);
  layout.shadowY = [-0.5, 3.5];
  return {
    root, layout,
    update(f) {
      dethrone = Math.max(0, dethrone - f.dt * 0.8);
      red.value = dethrone > 0 ? Math.min(1, dethrone * 1.6) * (0.75 + 0.25 * Math.sign(Math.sin(f.time * 18))) : 0;
    },
    onEvent(ev) {
      if (ev.type === EV.DETHRONE) {
        dethrone = 1;
        const p = peds[0];
        kit.particles.burst({ x: p.x, y: p.height + 0.2, z: p.y, count: 26, color: [...MEDALS, '#FF4F9A', '#4CB8FF'], speed: 4, up: 6, size: 0.08, life: 1.6, spread: 0.8, gravity: 9 });
      }
    },
    dispose() {
      for (const o of owned) o.dispose();
      for (const m of (root.userData.mats ?? []) as { dispose(): void }[]) m.dispose();
    },
  };
}

// ---- Podium marquee --------------------------------------------------------------------------

const MARQUEE = { x: 0, y: -7.7, h: 2.6, w: 8.6, ht: 2.3, tilt: 0.2 } as const;
const BULB_STEP = 0.42;

function paintMarquee(g: Ctx2D, w: number, h: number): void {
  g.fillStyle = INK;
  roundRect(g, 0, 0, w, h, h * 0.16);
  g.fill();
  g.fillStyle = TOY_RED;
  roundRect(g, 10, 10, w - 20, h - 20, h * 0.13);
  g.fill();
  g.fillStyle = '#C93A35';
  for (let k = 0; k < 18; k++) {
    g.save();
    g.translate(w / 2, h * 1.6);
    g.rotate(-Math.PI / 2 + ((k - 8.5) / 18) * 1.6);
    g.fillRect(0, -h * 0.05, h * 1.9, h * 0.1);
    g.restore();
  }
  g.strokeStyle = '#FFD24A';
  g.lineWidth = 6;
  roundRect(g, 34, 34, w - 68, h - 68, h * 0.1);
  g.stroke();
  for (const sx of [0.1, 0.9]) {
    g.fillStyle = '#FFD24A';
    starPath(g, w * sx, h * 0.52, h * 0.2);
    g.fill();
    g.strokeStyle = INK;
    g.lineWidth = 5;
    g.stroke();
  }
  stickerText(g, 'CHAMPIONS', w / 2, h * 0.53, h * 0.5, '#FFD24A');
}

/**
 * The CHAMPIONS marquee behind the Podium lid: a printed cardboard board on two balloons, a border
 * of bulbs (one draw call) chasing on the beat, three lamp heads on top. Returns the lamp points.
 */
function buildMarquee(kit: ArenaKit, root: Group, owned: { dispose(): void }[], red: IUniform<number>): { lamps: Vector3[] } {
  const M = MARQUEE;
  const frame = new Matrix4().makeRotationX(-M.tilt).setPosition(M.x, M.h, M.y);
  // Board: cardboard slab, printed face, ink outline.
  kit.batch.add(B.toon, new RoundedBoxGeometry(M.w + 0.3, M.ht + 0.3, 0.26, 2, 0.08), frame, { color: '#C98F55' });
  kit.batch.add(B.hullBold, new RoundedBoxGeometry(M.w + 0.3, M.ht + 0.3, 0.26, 1, 0.08), frame);
  const tex = canvasTexture(1024, 274, paintMarquee);
  owned.push(tex);
  const faceGeo = new PlaneGeometry(M.w, M.ht).applyMatrix4(new Matrix4().makeTranslation(0, 0, 0.14)).applyMatrix4(frame);
  const faceMat = surfaceMaterial(kit.env, kit.ramp, { key: 'podium-marquee', map: tex, rim: 0.25, gloss: 0.2 });
  const face = new Mesh(faceGeo, faceMat);
  face.name = 'marquee';
  root.add(face);
  owned.push(faceGeo, faceMat);
  // Bulbs around the printed face, chasing two-on two-off along the border.
  const pts: [number, number][] = [];
  const hw = M.w / 2 - 0.16;
  const hh = M.ht / 2 - 0.16;
  const per = 2 * (hw * 2 + hh * 2);
  const n = Math.round(per / BULB_STEP);
  for (let i = 0; i < n; i++) {
    let d = (i / n) * per;
    let x: number;
    let y: number;
    if (d < hw * 2) { x = -hw + d; y = hh; } else if ((d -= hw * 2) < hh * 2) { x = hw; y = hh - d; } else if ((d -= hh * 2) < hw * 2) { x = hw - d; y = -hh; } else { d -= hw * 2; x = -hw; y = -hh + d; }
    pts.push([x, y]);
  }
  const bulb = new SphereGeometry(0.085, kit.seg(8, 6), kit.seg(6, 4));
  const parts: BufferGeometry[] = [];
  pts.forEach(([x, y], i) => {
    const g = bulb.clone().applyMatrix4(new Matrix4().makeTranslation(x, y, 0.2)).applyMatrix4(frame);
    const idx = new Float32Array(g.getAttribute('position').count).fill(i);
    g.setAttribute('aIdx', new BufferAttribute(idx, 1));
    parts.push(g);
  });
  bulb.dispose();
  const bulbGeo = mergeKeep(parts, ['position', 'aIdx']);
  const bulbMat = new ShaderMaterial({
    uniforms: { ...UniformsUtils.clone(UniformsLib.fog), uBeat: kit.env.uBeat, uRed: red },
    vertexShader: /* glsl */ `
      attribute float aIdx;
      uniform float uBeat;
      varying float vOn;
      #include <common>
      #include <fog_pars_vertex>
      void main() {
        vOn = step(0.5, fract((aIdx - floor(uBeat * 4.0)) / 4.0));
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      uniform float uRed;
      varying float vOn;
      #include <common>
      #include <fog_pars_fragment>
      void main() {
        vec3 lit = mix(vec3(1.0, 0.92, 0.6) * 2.2, vec3(1.6, 0.08, 0.06), uRed);
        vec3 off = vec3(0.55, 0.32, 0.16);
        gl_FragColor = vec4(mix(off, lit, vOn), 1.0);
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
    fog: true, toneMapped: false,
  });
  const bulbs = new Mesh(bulbGeo, bulbMat);
  bulbs.name = 'marquee-bulbs';
  root.add(bulbs);
  owned.push(bulbGeo, bulbMat);
  // Two balloons hold it up; three lamp heads on the top edge.
  for (const sx of [-1, 1]) {
    const top = new Vector3(sx * (M.w / 2 - 0.5), M.ht / 2, 0).applyMatrix4(frame);
    const at = new Vector3(top.x + sx * 0.6, top.y + 2.2, top.z - 0.4);
    kit.batch.add(B.bg, new SphereGeometry(0.62, kit.seg(14, 8), kit.seg(10, 6)), new Matrix4().makeScale(1, 1.18, 1).setPosition(at.x, at.y, at.z), { color: sx < 0 ? '#FFD24A' : TOY_RED });
    kit.batch.add(B.bg, new ConeGeometry(0.1, 0.16, 6), new Matrix4().makeRotationX(Math.PI).setPosition(at.x, at.y - 0.8, at.z), { color: sx < 0 ? '#FFD24A' : TOY_RED });
    const dir = at.clone().add(new Vector3(0, -0.84, 0)).sub(top);
    const len = dir.length();
    const string = new CylinderGeometry(0.015, 0.015, len, 3);
    string.applyQuaternion(new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), dir.normalize()));
    kit.batch.add(B.bg, string, new Matrix4().makeTranslation((top.x + at.x) / 2, (top.y + at.y - 0.84) / 2, (top.z + at.z) / 2), { color: '#5A4A6A' });
    if (kit.rich) kit.batch.add(B.bgHull, new SphereGeometry(0.62, 10, 8), new Matrix4().makeScale(1, 1.18, 1).setPosition(at.x, at.y, at.z));
  }
  const peds = PODIUM_TUNING.pedestals;
  const lamps = peds.map((p) => {
    const base = new Vector3(Math.max(-M.w / 2 + 0.8, Math.min(M.w / 2 - 0.8, p.x * 1.15)), M.ht / 2 + 0.15, 0.05).applyMatrix4(frame);
    const aim = new Vector3(p.x, p.height, p.y).sub(base).normalize();
    const can = new CylinderGeometry(0.22, 0.3, 0.55, kit.seg(14, 8));
    can.applyQuaternion(new Quaternion().setFromUnitVectors(new Vector3(0, -1, 0), aim));
    kit.batch.add(B.chrome, can, new Matrix4().makeTranslation(base.x, base.y + 0.1, base.z), { color: '#3A3346' });
    kit.batch.add(B.glow, new CylinderGeometry(0.24, 0.24, 0.02, kit.seg(14, 8)).applyQuaternion(new Quaternion().setFromUnitVectors(new Vector3(0, -1, 0), aim)),
      new Matrix4().makeTranslation(base.x + aim.x * 0.29, base.y + 0.1 + aim.y * 0.29, base.z + aim.z * 0.29), { color: '#FFF6D8' });
    return new Vector3(base.x + aim.x * 0.3, base.y + 0.1 + aim.y * 0.3, base.z + aim.z * 0.3);
  });
  return { lamps };
}
